/**
 * Display Formatting Helpers
 * 
 * Frontend utilities for displaying currency, dates, times, and phone numbers
 * with internationalization support (US, CA, GB, NG).
 * 
 * Uses browser's built-in Intl API (no external libraries needed).
 */

/**
 * Format currency for display
 * 
 * @param {Number} amount - Amount to format (e.g., 75, 95.50)
 * @param {String} currency - ISO currency code (USD, CAD, GBP, NGN)
 * @returns {String} Formatted currency string
 * 
 * @example
 * formatCurrency(75, 'USD')        → "$75.00"
 * formatCurrency(95.50, 'CAD')     → "CA$95.50"
 * formatCurrency(60, 'GBP')        → "£60.00"
 * formatCurrency(28000, 'NGN')     → "₦28,000.00"
 */
function formatCurrency(amount, currency = 'USD') {
  // Get country code from currency for locale
  const localeMap = {
    'USD': 'en-US',
    'CAD': 'en-CA',
    'GBP': 'en-GB',
    'NGN': 'en-NG'
  };

  const locale = localeMap[currency] || 'en-US';

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return formatter.format(amount);
  } catch (error) {
    // Fallback: simple format with currency symbol
    return `${getCurrencySymbol(currency)}${parseFloat(amount).toFixed(2)}`;
  }
}

/**
 * Get currency symbol
 * 
 * @param {String} currency - ISO currency code
 * @returns {String} Currency symbol
 */
function getCurrencySymbol(currency) {
  const symbols = {
    'USD': '$',
    'CAD': 'CA$',
    'GBP': '£',
    'NGN': '₦'
  };

  return symbols[currency] || currency;
}

/**
 * Format date with timezone
 * 
 * @param {Date|String} date - Date to format
 * @param {String} timezone - IANA timezone (e.g., 'America/Toronto')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {String} Formatted date string
 * 
 * @example
 * formatDate('2026-02-01', 'America/Toronto')          → "February 1, 2026"
 * formatDate('2026-02-01', 'Africa/Lagos', {month: 'short'}) → "Feb 1, 2026"
 */
function formatDate(date, timezone = 'America/New_York', options = {}) {
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone
  };

  const formatOptions = { ...defaultOptions, ...options };

  try {
    const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
    return formatter.format(new Date(date));
  } catch (error) {
    return new Date(date).toLocaleDateString();
  }
}

/**
 * Format time with timezone
 * 
 * @param {Date|String} dateTime - Date/time to format
 * @param {String} timezone - IANA timezone
 * @param {Boolean} show24Hour - Use 24-hour format (default: false)
 * @returns {String} Formatted time string
 * 
 * @example
 * formatTime('2026-02-01T10:00:00Z', 'America/Toronto')   → "5:00 AM EST"
 * formatTime('2026-02-01T10:00:00Z', 'Africa/Lagos')      → "11:00 AM WAT"
 * formatTime('2026-02-01T10:00:00Z', 'Europe/London', true) → "10:00 GMT"
 */
function formatTime(dateTime, timezone = 'America/New_York', show24Hour = false) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: !show24Hour,
      timeZone: timezone,
      timeZoneName: 'short'
    });

    return formatter.format(new Date(dateTime));
  } catch (error) {
    return new Date(dateTime).toLocaleTimeString();
  }
}

/**
 * Format date and time together
 * 
 * @param {Date|String} dateTime - Date/time to format
 * @param {String} timezone - IANA timezone
 * @returns {String} Formatted date and time string
 * 
 * @example
 * formatDateTime('2026-02-01T10:00:00Z', 'America/Toronto')
 * → "Saturday, February 1, 2026 at 5:00 AM EST"
 */
function formatDateTime(dateTime, timezone = 'America/New_York') {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    });

    return formatter.format(new Date(dateTime));
  } catch (error) {
    return new Date(dateTime).toLocaleString();
  }
}

/**
 * Format phone number for display
 * 
 * @param {String} phoneE164 - Phone in E.164 format (+14165551234)
 * @param {String} format - Display format ('international', 'national', 'short')
 * @returns {String} Formatted phone string
 * 
 * @example
 * formatPhoneDisplay('+14165551234', 'international') → "+1 416 555 1234"
 * formatPhoneDisplay('+14165551234', 'national')      → "(416) 555-1234"
 * formatPhoneDisplay('+447946095812', 'international') → "+44 7946 095812"
 */
function formatPhoneDisplay(phoneE164, format = 'international') {
  if (!phoneE164 || !phoneE164.startsWith('+')) {
    return phoneE164 || ''; // Return as-is if not E.164
  }

  // Remove + for processing
  const digits = phoneE164.substring(1);

  if (format === 'international') {
    // +1 416 555 1234
    if (digits.startsWith('1') && digits.length === 11) {
      // US/CA
      return `+1 ${digits.substring(1, 4)} ${digits.substring(4, 7)} ${digits.substring(7)}`;
    } else if (digits.startsWith('44') && digits.length === 12) {
      // UK
      return `+44 ${digits.substring(2, 6)} ${digits.substring(6, 9)}${digits.substring(9)}`;
    } else if (digits.startsWith('234') && digits.length === 13) {
      // Nigeria
      return `+234 ${digits.substring(3, 6)} ${digits.substring(6, 9)} ${digits.substring(9)}`;
    }
  } else if (format === 'national') {
    // (416) 555-1234
    if (digits.startsWith('1') && digits.length === 11) {
      return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    } else if (digits.startsWith('44') && digits.length === 12) {
      return `0${digits.substring(2, 6)} ${digits.substring(6, 9)}${digits.substring(9)}`;
    } else if (digits.startsWith('234') && digits.length === 13) {
      return `0${digits.substring(3, 6)} ${digits.substring(6, 9)} ${digits.substring(9)}`;
    }
  }

  // Fallback: return with spaces every 4 digits
  return phoneE164.replace(/(\d{4})/g, '$1 ').trim();
}

/**
 * Get country name from code
 * 
 * @param {String} countryCode - ISO country code (US, CA, GB, NG)
 * @returns {String} Country name
 */
function getCountryName(countryCode) {
  const countries = {
    'US': 'United States',
    'CA': 'Canada',
    'GB': 'United Kingdom',
    'NG': 'Nigeria'
  };

  return countries[countryCode] || countryCode;
}

/**
 * Get country flag emoji
 * 
 * @param {String} countryCode - ISO country code
 * @returns {String} Flag emoji
 */
function getCountryFlag(countryCode) {
  const flags = {
    'US': '🇺🇸',
    'CA': '🇨🇦',
    'GB': '🇬🇧',
    'NG': '🇳🇬'
  };

  return flags[countryCode] || '🌍';
}

/**
 * Format price range for display
 * 
 * @param {Number} minPrice - Minimum price
 * @param {Number} maxPrice - Maximum price
 * @param {String} currency - ISO currency code
 * @returns {String} Formatted price range
 * 
 * @example
 * formatPriceRange(50, 150, 'USD') → "$50 - $150"
 * formatPriceRange(75, 75, 'CAD')  → "CA$75"
 */
function formatPriceRange(minPrice, maxPrice, currency = 'USD') {
  if (minPrice === maxPrice) {
    return formatCurrency(minPrice, currency);
  }

  const symbol = getCurrencySymbol(currency);
  return `${symbol}${minPrice} - ${symbol}${maxPrice}`;
}

/**
 * Format time string with timezone abbreviation
 * 
 * @param {String} timeString - Time in HH:MM format (e.g., "14:00")
 * @param {String} date - Date string (YYYY-MM-DD) to determine correct timezone offset
 * @param {String} timezone - IANA timezone (e.g., 'America/New_York')
 * @returns {String} Formatted time with timezone (e.g., "2:00 PM EST")
 * 
 * @example
 * formatTimeWithTimezone("14:00", "2026-01-20", "America/New_York") → "2:00 PM EST"
 * formatTimeWithTimezone("09:30", "2026-06-20", "America/Toronto") → "9:30 AM EDT"
 * formatTimeWithTimezone("16:00", "2026-01-20", "Africa/Lagos") → "4:00 PM WAT"
 */
function formatTimeWithTimezone(timeString, date, timezone = 'America/New_York') {
  try {
    // Combine date and time to get proper timezone offset
    const dateTimeString = `${date}T${timeString}:00`;
    const dateTime = new Date(dateTimeString);
    
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    });

    return formatter.format(dateTime);
  } catch (error) {
    // Fallback: just format the time without timezone
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  }
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * 
 * @param {Date|String} date - Date to format
 * @param {String} locale - Locale code (default: 'en-US')
 * @returns {String} Relative time string
 * 
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000)) → "1 hour ago"
 * formatRelativeTime(new Date(Date.now() + 86400000)) → "in 1 day"
 */
function formatRelativeTime(date, locale = 'en-US') {
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const now = new Date();
    const diff = new Date(date) - now;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (Math.abs(days) > 0) {
      return rtf.format(days, 'day');
    } else if (Math.abs(hours) > 0) {
      return rtf.format(hours, 'hour');
    } else if (Math.abs(minutes) > 0) {
      return rtf.format(minutes, 'minute');
    } else {
      return rtf.format(seconds, 'second');
    }
  } catch (error) {
    return date.toString();
  }
}

// Export for use in React components
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    formatCurrency,
    getCurrencySymbol,
    formatDate,
    formatTime,
    formatDateTime,
    formatTimeWithTimezone,
    formatPhoneDisplay,
    getCountryName,
    getCountryFlag,
    formatPriceRange,
    formatRelativeTime
  };
}

// Also export as ES6 modules for frontend
export {
  formatCurrency,
  getCurrencySymbol,
  formatDate,
  formatTime,
  formatDateTime,
  formatTimeWithTimezone,
  formatPhoneDisplay,
  getCountryName,
  getCountryFlag,
  formatPriceRange,
  formatRelativeTime
};
